import { useCallback, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileDown, PenLine, ShieldCheck, ShieldAlert, UserPlus, AlertCircle } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Checkbox } from '@/app/components/ui/checkbox';
import { useBranding } from '@/contexts/branding-context';
import {
  useToolboxTalk,
  useGuestSignToolboxTalk,
} from '@/hooks/use-safety';
import { useKioskSign, type KioskSignResult } from '@/hooks/use-kiosk-sign';
import { SignaturePad } from '@/components/safety/signature-pad';
import { OfflineSignatureIndicator } from '@/app/components/offline/offline-signature-indicator';
import { apiClient } from '@/lib/api-client';
import { filenameFromContentDisposition } from '@/lib/download';
import { queryKeys } from '@/lib/query-client';
import { toast } from '@/app/components/ui/simple-toaster';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';

type SignMode = 'none' | 'authenticated' | 'guest';

const MATCH_CONFIDENCE_SCORES: Record<string, number> = {
  tech: 1,
  email: 0.8,
  name_only: 0.4,
  new: 0.2,
};
const LOW_CONFIDENCE_THRESHOLD = 0.6;

function isLowConfidence(matchConfidence: string | null | undefined): boolean {
  const score = matchConfidence ? MATCH_CONFIDENCE_SCORES[matchConfidence] ?? 0 : 0;
  return score < LOW_CONFIDENCE_THRESHOLD;
}

export function ToolboxTalkDetailPage() {
  const timeZone = useOrgTimeZone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const talkId = parseInt(id || '0', 10);
  const { data: talk, isLoading } = useToolboxTalk(talkId);
  const queryClient = useQueryClient();
  const guestSignMutation = useGuestSignToolboxTalk();
  const { branding } = useBranding();

  const pinMode = branding?.kiosk_pin_enabled ?? false;

  const [signMode, setSignMode] = useState<SignMode>('none');

  // Guest sign-in fields
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');

  const [downloading, setDownloading] = useState(false);
  const [kitInspection, setKitInspection] = useState(false);

  const handleDownloadPdf = async () => {
    if (!talk) return;
    setDownloading(true);
    try {
      const response = await apiClient.get(`/api/v1/safety/toolbox-talks/${talk.id}/pdf/`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        filenameFromContentDisposition(
          response.headers['content-disposition'],
          `ToolboxTalk-${talk.id}.pdf`,
        ),
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // silently fail
    } finally {
      setDownloading(false);
    }
  };

  const kioskSign = useKioskSign({
    pinMode,
    documentId: talkId,
    endpoint: `/api/v1/safety/toolbox-talks/${talkId}/kiosk-sign/`,
    bodyBase: { kit_inspection: kitInspection },
    onSuccess: useCallback((result: KioskSignResult) => {
      if (!talk) return;
      setSignMode('none');
      setKitInspection(false);
      if (result.queued) {
        toast.success('Sign-in queued — will sync when you\'re back online');
      } else {
        void queryClient.invalidateQueries({ queryKey: queryKeys.toolboxTalks.detail(talk.id) });
      }
    }, [talk, queryClient]),
  });

  const resetSignForm = () => {
    setSignMode('none');
    kioskSign.clear();
    setKitInspection(false);
    setGuestName('');
    setGuestEmail('');
  };

  const handleAuthenticatedSignIn = () => {
    if (!talk || !kioskSign.credentialsComplete || !kioskSign.signatureData || kioskSign.submitting) return;
    void kioskSign.handleSubmit();
  };

  const handleGuestSignature = (dataUrl: string) => {
    if (!talk || !guestName || guestSignMutation.isPending) return;
    guestSignMutation.mutate(
      {
        id: talk.id,
        data: {
          attendee_name: guestName,
          attendee_email: guestEmail || undefined,
          signature_data: dataUrl,
        },
      },
      {
        onSuccess: () => {
          setGuestName('');
          setGuestEmail('');
          setSignMode('none');
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!talk) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-600">Toolbox talk not found.</p>
        <Button asChild className="mt-4">
          <Link to="/safety/toolbox-talks">Back to Toolbox Talks</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/safety/toolbox-talks')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{talk.title}</h1>
            <p className="text-gray-600">
              {formatDateOnly(talk.date)}
              {talk.location && ` \u00b7 ${talk.location}`}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={handleDownloadPdf} disabled={downloading}>
          <FileDown className="h-4 w-4 mr-1" />
          PDF
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Presented By:</span> {talk.presented_by_username}
            </div>
            {talk.duration_minutes && (
              <div>
                <span className="text-gray-500">Duration:</span> {talk.duration_minutes} minutes
              </div>
            )}
          </div>
          {talk.notes && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Notes</p>
              <p className="text-sm whitespace-pre-wrap">{talk.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {talk.topics.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Topics Covered ({talk.topics.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {talk.topics.map((topic) => (
              <div key={topic.id} className="border-l-2 border-blue-300 pl-4">
                <h3 className="font-medium">{topic.topic}</h3>
                {topic.description && (
                  <p className="text-sm text-gray-600 mt-1">{topic.description}</p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Attendees / Sign-In Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Attendees ({talk.attendees.length})</CardTitle>
          {signMode === 'none' && (
            <div className="flex gap-2">
              <Button size="sm" onClick={() => setSignMode('authenticated')}>
                <PenLine className="h-4 w-4 mr-1" />
                Sign In
              </Button>
              <Button size="sm" variant="outline" onClick={() => setSignMode('guest')}>
                <UserPlus className="h-4 w-4 mr-1" />
                Guest
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <OfflineSignatureIndicator />
          {talk.attendees.length > 0 && (
            <div className="space-y-4">
              {talk.attendees.map((attendee) => (
                <div key={attendee.id} className="flex items-start gap-4 p-3 border rounded-md">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{attendee.attendee_name}</p>
                      {attendee.is_verified ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                          <ShieldCheck className="h-3 w-3" />
                          Verified
                          {attendee.auth_method && ` (${attendee.auth_method})`}
                        </span>
                      ) : attendee.signed_at ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                          <ShieldAlert className="h-3 w-3" />
                          Unverified
                        </span>
                      ) : null}
                      {attendee.kit_inspection_signed && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                          Kit ✓
                        </span>
                      )}
                      {attendee.is_verified && isLowConfidence(attendee.match_confidence) && (
                        <span
                          className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"
                          title="This sign-in was matched with low confidence and should be reviewed."
                        >
                          <AlertCircle className="h-3 w-3" />
                          Low confidence
                        </span>
                      )}
                    </div>
                    {attendee.attendee_email && (
                      <p className="text-sm text-gray-600">{attendee.attendee_email}</p>
                    )}
                    {attendee.signed_at && (
                      <p className="text-xs text-gray-400">
                        {formatDateTimeInTimeZone(attendee.signed_at, timeZone)}
                      </p>
                    )}
                  </div>
                  {attendee.signature_data && (
                    <SignaturePad
                      readOnly
                      signatureData={attendee.signature_data}
                      onAccept={() => {}}
                      width={200}
                      height={70}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {talk.attendees.length === 0 && signMode === 'none' && (
            <p className="text-sm text-gray-500">No attendees have signed in yet.</p>
          )}

          {/* Authenticated sign-in form (kiosk mode) */}
          {signMode === 'authenticated' && (
            <div className="border rounded-md p-4 space-y-4 bg-gray-50">
              <h4 className="font-medium text-sm">Authenticated Sign-In</h4>
              <p className="text-xs text-gray-500">
                Enter your username and PIN or password, then sign to sign in.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="kiosk-username">Username *</Label>
                  <Input
                    id="kiosk-username"
                    value={kioskSign.username}
                    onChange={(e) => kioskSign.setUsername(e.target.value)}
                    placeholder="Username"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <Label htmlFor="kiosk-credential">
                    {kioskSign.usePinField ? '6-Digit PIN' : 'Password'} *
                  </Label>
                  <Input
                    id="kiosk-credential"
                    type={kioskSign.usePinField ? 'tel' : 'password'}
                    inputMode={kioskSign.usePinField ? 'numeric' : undefined}
                    maxLength={kioskSign.usePinField ? 6 : undefined}
                    value={kioskSign.credential}
                    onChange={(e) => {
                      const value = e.target.value;
                      kioskSign.setCredential(kioskSign.usePinField ? value.replace(/\D/g, '').slice(0, 6) : value);
                    }}
                    placeholder={kioskSign.usePinField ? '000000' : 'Password'}
                    autoComplete="off"
                  />
                </div>
              </div>
              {kioskSign.error && (
                <p className="text-sm text-red-600">{kioskSign.error}</p>
              )}
              <div className="flex items-center gap-2">
                <Checkbox
                  id="kit-inspection"
                  checked={kitInspection}
                  onCheckedChange={(checked) => setKitInspection(checked === true)}
                />
                <Label htmlFor="kit-inspection" className="text-sm font-normal">
                  I inspected my personal kit today
                </Label>
              </div>
              <div className="space-y-2">
                <Label>Signature</Label>
                <SignaturePad
                  onAccept={kioskSign.setSignatureData}
                  onClear={() => kioskSign.setSignatureData('')}
                  acceptDisabled={!kioskSign.credentialsComplete}
                  width={400}
                  height={120}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={resetSignForm}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleAuthenticatedSignIn}
                  disabled={!kioskSign.credentialsComplete || !kioskSign.signatureData || kioskSign.submitting}
                >
                  {kioskSign.submitting ? 'Signing in...' : 'Sign In'}
                </Button>
              </div>
            </div>
          )}

          {/* Guest sign-in form */}
          {signMode === 'guest' && (
            <div className="border rounded-md p-4 space-y-4 bg-amber-50/50">
              <div className="flex items-center gap-2">
                <h4 className="font-medium text-sm">Guest / Visitor Sign-In</h4>
                <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5">
                  <ShieldAlert className="h-3 w-3" />
                  Unverified
                </span>
              </div>
              <p className="text-xs text-gray-500">
                For visitors or non-platform attendees. This entry will be marked as unverified.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="guest-name">Name *</Label>
                  <Input
                    id="guest-name"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <Label htmlFor="guest-email">Email</Label>
                  <Input
                    id="guest-email"
                    type="email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    placeholder="Optional"
                  />
                </div>
              </div>
              {guestName ? (
                <SignaturePad onAccept={handleGuestSignature} acceptDisabled={guestSignMutation.isPending} />
              ) : (
                <p className="text-sm text-gray-500">Enter your name to sign in.</p>
              )}
              <div className="flex justify-end">
                <Button variant="ghost" size="sm" onClick={resetSignForm}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
